import cn from 'classnames';
import { ReactNode } from 'react';
import { Form } from 'react-bootstrap';
import { MdAdd, MdClose } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@src/Components/IconButton';
import { InfoTooltip } from '@src/Components/InfoTooltip';
import { SegmentedControlButton } from '@src/Components/SegmentedControlButton';
import { formatOutcomeLabel } from '../../forecasting-utils';
import { ComparisonBudgetRow } from './ComparisonBudgetRow';
import {
  ALL_PLATFORMS,
  ALL_TACTICS,
  Analysis,
  AnalysisType,
  AnchorStatusType,
  Card,
  Grain,
  Viability
} from './request-form-constants';
import {
  availableOutcomes,
  buildPlatformOptions,
  buildTacticOptions,
  isComparisonRemovable,
  isPlatformPooled,
  isTacticPooled,
  scalingOutcomesAtGrain
} from './request-form-utils';
import { ScalingBudgetFields } from './ScalingBudgetFields';
import { HighestWeek } from './scaling-request-utils';

/** Whether each side of the Forecast | Scaling analysis selector is usable, and why not. */
export interface AnalysisAvailability {
  forecast: { ok: boolean; reason: string };
  scaling: { ok: boolean; reason: string };
}

interface RequestCardProps extends Card {
  /** The menus for this card's analysis: forecast ladder, or scaling ladder for a scaling card. */
  viability: Viability;
  patchCard: (id: number, patch: Partial<Card>) => void;
  analysisAvailability: AnalysisAvailability;
  onSwitchAnalysis: (analysis: AnalysisType) => void;
  /** A Channel/Platform/Tactic/KPI change. RequestForm re-prefills a scaling
   *  card's max weekly spend against the new selection when it can. */
  onSelectionChange: (patch: Partial<Card>) => void;
  /** Scaling cards only: the highest full week at the card's grain (the spend anchor). */
  highestWeek: HighestWeek | null;
  anchorStatus: AnchorStatusType;
  onRetryAnchor: () => void;
  kpis: string[];
  channels: string[];
  /** Whether this card may be removed (false when it's the only card). */
  canRemove: boolean;
  onRemove: () => void;
  onAddComparison: () => void;
}

export const RequestCard = ({
  viability,
  patchCard,
  kpis,
  channels,
  canRemove,
  onRemove,
  onAddComparison,
  analysisAvailability,
  onSwitchAnalysis,
  onSelectionChange,
  highestWeek,
  anchorStatus,
  onRetryAnchor,
  id,
  channel,
  platform,
  tactic,
  kpi,
  grain,
  forecast,
  scaling,
  primary,
  comparisons,
  volume,
  revenue,
  maxWeeklySpend,
  budgetPeriod
}: RequestCardProps): ReactNode => {
  const {
    tacticsFor,
    tacticsForChannel,
    platformViableForKpi,
    tacticViableForKpi,
    allTacticsViableForKpi,
    channelViableForKpi,
    comboViableAtGrain,
    firstChannelTactic,
    platformsByChannel,
    bestTacticFor,
    defaultSelection,
    cardInfoFor
  } = viability;
  const platforms = platformsByChannel[channel] ?? [];
  // Channel-only (scaling cards) pools both.
  const platformPooled = isPlatformPooled(grain);
  const tacticPooled = isTacticPooled(grain);
  const platformValue = platformPooled ? ALL_PLATFORMS : platform;
  const tacticValue = tacticPooled ? ALL_TACTICS : tactic;
  const tacticSourceList = platformPooled
    ? tacticsForChannel(channel)
    : tacticsFor(channel, platform);

  const platformOptions = buildPlatformOptions(viability, platforms, channel, kpi);
  const tacticOptions = buildTacticOptions(
    viability,
    tacticSourceList,
    channel,
    platform,
    kpi,
    platformPooled
  );

  const info = cardInfoFor(channel, platform, tactic, kpi);
  // A combo can be viable overall while one outcome has no usable history, so
  // gate the Volume/Revenue checkboxes on per-outcome eligibility. A scaling
  // card's info is off the scaling ladder, gated at its own grain.
  const outcomes = scaling
    ? scalingOutcomesAtGrain(grain, info)
    : availableOutcomes({ forecast, scaling }, grain, info);
  // A scaling card's anchor moves with its selection, so RequestForm may
  // re-prefill its max weekly spend; a forecast card's patch is exactly as before.
  const patchSelection = (patch: Partial<Card>) =>
    scaling ? onSelectionChange(patch) : patchCard(id, patch);
  const primaryNum = Number(primary) || 0;
  const removableComparisonsCount = comparisons.filter(cp => !cp.locked).length;

  return (
    <div className={cn('req-card', { 'is-scaling': scaling })} data-testid='request-card'>
      <div className='req-card-head'>
        <div className='req-card-selects'>
          <div className='req-field'>
            <span className='req-label'>Channel</span>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              label='Channel'
              value={channel}
              options={channels}
              onChange={(ch: string) => {
                const { platform: pl, tactic: tac, grain: g } = defaultSelection(ch, kpi);
                patchSelection({ channel: ch, platform: pl, tactic: tac, grain: g });
              }}
            />
          </div>
          <div className='req-field'>
            <span className='req-label'>Platform</span>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              label='Platform'
              value={platformValue}
              options={platformOptions}
              onChange={(pl: string) => {
                if (pl === ALL_PLATFORMS) {
                  // "All tactics" becomes invalid once platform is pooled (forecast)
                  // -- resolve to a real, channel-wide-viable tactic.
                  const channelTactic = tacticViableForKpi(channel, tactic, kpi)
                    ? tactic
                    : firstChannelTactic(channel, kpi);
                  // Scaling only (never viable for forecast): keep "All tactics" as
                  // channel-only, or fall back to it when no single tactic pools.
                  if (channelViableForKpi(channel, kpi) && (tacticPooled || !channelTactic)) {
                    patchSelection({ grain: Grain.CHANNEL });
                    return;
                  }
                  patchSelection({ grain: Grain.CHANNEL_TACTIC, tactic: channelTactic ?? tactic });
                  return;
                }
                if (tacticPooled && allTacticsViableForKpi(channel, pl, kpi)) {
                  // Keep "All tactics" under the new platform when still viable.
                  patchSelection({ platform: pl, grain: Grain.CHANNEL_PLATFORM });
                  return;
                }
                if (
                  !tacticPooled &&
                  tacticsFor(channel, pl).includes(tactic) &&
                  comboViableAtGrain(channel, pl, tactic, kpi, Grain.FINEST)
                ) {
                  patchSelection({ platform: pl, tactic, grain: Grain.FINEST });
                  return;
                }
                const { tactic: tac, grain: g } = bestTacticFor(channel, pl, kpi);
                patchSelection({ platform: pl, tactic: tac, grain: g });
              }}
            />
          </div>
          <div className='req-field'>
            <span className='req-label'>Tactic</span>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              label='Tactic'
              value={tacticValue}
              options={tacticOptions}
              onChange={(t: string) => {
                if (t === ALL_TACTICS) {
                  // Under "All platforms" this is channel-only, which only a
                  // scaling card's menu enables.
                  patchSelection({
                    grain: platformPooled ? Grain.CHANNEL : Grain.CHANNEL_PLATFORM
                  });
                  return;
                }
                // Under "All platforms", picking a real tactic IS the
                // All-platforms + tactic (channel_tactic) selection.
                patchSelection({
                  tactic: t,
                  grain: platformPooled ? Grain.CHANNEL_TACTIC : Grain.FINEST
                });
              }}
            />
          </div>
          <div className='req-field'>
            <span className='req-label'>KPI</span>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              label='KPI'
              value={kpi}
              options={kpis}
              onChange={(k: string) => {
                if (comboViableAtGrain(channel, platform, tactic, k, grain)) {
                  patchSelection({ kpi: k });
                  return;
                }
                // Preserve platform only if it's the real (non-pooled)
                // selection -- it's stale leftover data while platformPooled.
                if (!platformPooled && platformViableForKpi(channel, platform, k)) {
                  if (tacticPooled && allTacticsViableForKpi(channel, platform, k)) {
                    patchSelection({ kpi: k });
                    return;
                  }
                  const { tactic: tac, grain: g } = bestTacticFor(channel, platform, k);
                  patchSelection({ kpi: k, tactic: tac, grain: g });
                  return;
                }
                const { platform: pl, tactic: tac, grain: g } = defaultSelection(channel, k);
                patchSelection({ kpi: k, platform: pl, tactic: tac, grain: g });
              }}
            />
          </div>
        </div>
        {canRemove && (
          <IconButton appearance={Appearance.EMPTY} design={Design.NEUTRAL} onClick={onRemove}>
            <MdClose />
          </IconButton>
        )}
      </div>

      {/* Analysis type (QA-gated, one per card) + outcome variable. Outcome
          defaults to Volume; Revenue is opt-in and both are allowed — each
          selected outcome becomes its own forecast. */}
      <div className='req-analyses'>
        <Form.Group className='req-analysis-group'>
          <Form.Label>Analysis</Form.Label>
          <SegmentedControlButton<AnalysisType>
            size={Size.MD}
            className='req-analysis-selector'
            options={[
              {
                key: Analysis.FORECAST,
                label: 'Forecast',
                disabled: !analysisAvailability.forecast.ok
              },
              {
                key: Analysis.SCALING,
                label: 'Scaling analysis',
                disabled: !analysisAvailability.scaling.ok
              }
            ]}
            selectedOption={scaling ? Analysis.SCALING : Analysis.FORECAST}
            onChange={onSwitchAnalysis}
          />
          {!analysisAvailability.forecast.ok && (
            <span className='req-analysis-reason' data-testid='req-forecast-reason'>
              {analysisAvailability.forecast.reason}
            </span>
          )}
          {!analysisAvailability.scaling.ok && (
            <span className='req-analysis-reason' data-testid='req-scaling-reason'>
              {analysisAvailability.scaling.reason}
            </span>
          )}
        </Form.Group>
        <Form.Group className='req-analysis-group'>
          <Form.Label>Outcome</Form.Label>
          <Checkbox
            id={`req-vol-${id}`}
            checked={volume && outcomes.volume}
            onChange={e => patchCard(id, { volume: e.target.checked })}
            disabled={!outcomes.volume}
            label={`${formatOutcomeLabel(kpi, 'spend_volume')}${
              outcomes.volume ? '' : ' (not enough history)'
            }`}
          />
          <Checkbox
            id={`req-rev-${id}`}
            checked={revenue && outcomes.revenue}
            onChange={e => patchCard(id, { revenue: e.target.checked })}
            disabled={!outcomes.revenue}
            label={
              <>
                {formatOutcomeLabel(kpi, 'spend_revenue')}
                {!outcomes.revenue && ' (not enough history)'}
                <InfoTooltip>
                  Revenue reflects the platform attributed value for this KPI. For lower-funnel KPIs
                  like purchases, that&apos;s the transaction revenue the platform credits to the
                  ad. For mid- and upper-funnel KPIs like visits or add-to-carts, it&apos;s the
                  value the platform assigns to the event itself.
                </InfoTooltip>
              </>
            }
          />
          {(forecast || scaling) &&
            !(volume && outcomes.volume) &&
            !(revenue && outcomes.revenue) && <span className='req-error'>Pick at least one</span>}
        </Form.Group>
      </div>

      {scaling ? (
        <div className='req-budgets'>
          <ScalingBudgetFields
            maxWeeklySpend={maxWeeklySpend}
            budgetPeriod={budgetPeriod}
            highestWeek={highestWeek}
            anchorStatus={anchorStatus}
            onRetryAnchor={onRetryAnchor}
            onChange={patch => patchCard(id, patch)}
          />
        </div>
      ) : (
        <div className='req-budgets'>
          <div className='req-field req-primary'>
            <span className='req-label'>Primary budget</span>
            <div className='req-money'>
              <span>$</span>
              <Form.Control
                type='number'
                value={primary}
                min={0}
                onChange={e => patchCard(id, { primary: e.target.value })}
              />
            </div>
          </div>

          <div className='req-comparisons'>
            <span className='req-label'>Comparison budgets</span>
            {comparisons.map(cp => (
              <ComparisonBudgetRow
                key={cp.id}
                cp={cp}
                primaryNum={primaryNum}
                onChangePct={pct =>
                  patchCard(id, {
                    comparisons: comparisons.map(x => (x.id === cp.id ? { ...x, pct } : x))
                  })
                }
                onRemove={() =>
                  patchCard(id, {
                    comparisons: comparisons.filter(x => x.id !== cp.id)
                  })
                }
                removable={!cp.locked && isComparisonRemovable(removableComparisonsCount)}
              />
            ))}
            <Button
              size={Size.LG}
              appearance={Appearance.OUTLINED}
              leadingElement={<MdAdd />}
              onClick={onAddComparison}
            >
              Add comparison budget
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
